import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { X, Calendar, Clock, ClipboardList, UploadCloud } from "lucide-react";
import {
  fetchLocations,
  fetchTasks,
  createTaskSubmit,
} from "../../redux/actions/taskActions";

const TaskApprovalModalPage = ({ onClose, onTaskAdded }) => {
  const dispatch = useDispatch();
  const { locations, tasks } = useSelector((state) => state.task);

  const [form, setForm] = useState({
    location_id: "", // this will be purpose.id
    task_name: [], // array of location_name
    createTaskDate: "",
    endTaskDate: "",
    start_time: "",
    end_time: "",
    date: "",
    time: "",
    other_purpose: "", // for custom purpose when "Others" is selected
  });

  const [file, setFile] = useState(null);
  const [showOtherInput, setShowOtherInput] = useState(false);

  useEffect(() => {
    dispatch(fetchLocations());
    dispatch(fetchTasks());

    const now = new Date();
    const today = now.toISOString().split("T")[0];
    const time = now.toTimeString().slice(0, 5);

    setForm((prev) => ({
      ...prev,
      createTaskDate: today,
      endTaskDate: today,
      date: today,
      start_time: time,
      end_time: time,
      time,
    }));
  }, [dispatch]);

  // ✅ Image compression function
  const compressImage = (file, maxWidth = 800, quality = 0.8) => {
    return new Promise((resolve) => {
      // If it's not an image file, return as is
      if (!file.type.startsWith("image/")) {
        resolve(file);
        return;
      }

      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      const img = new Image();

      img.onload = () => {
        // Calculate new dimensions
        let { width, height } = img;

        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }

        // Set canvas dimensions
        canvas.width = width;
        canvas.height = height;

        // Draw and compress
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            // Create a new File object with the compressed blob
            const compressedFile = new File([blob], file.name, {
              type: file.type,
              lastModified: Date.now(),
            });
            resolve(compressedFile);
          },
          file.type,
          quality
        );
      };

      img.src = URL.createObjectURL(file);
    });
  };

  const handleLocationChange = (e) => {
    const selectedOptions = Array.from(e.target.selectedOptions);
    const selectedNames = selectedOptions.map((opt) => opt.label);
    setForm((prev) => ({
      ...prev,
      task_name: selectedNames,
    }));
  };

  const handlePurposeChange = (e) => {
    const value = e.target.value;

    if (value === "Others") {
      setShowOtherInput(true);
      setForm((prev) => ({
        ...prev,
        location_id: value,
      }));
    } else {
      setShowOtherInput(false);
      setForm((prev) => ({
        ...prev,
        location_id: value,
        other_purpose: "", // Clear other_purpose when not selecting "Others"
      }));
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // ✅ Updated file handler with compression
  const handleFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      try {
        const compressedFile = await compressImage(selectedFile);
        console.log(
          `Original size: ${(selectedFile.size / 1024 / 1024).toFixed(2)}MB`
        );
        console.log(
          `Compressed size: ${(compressedFile.size / 1024 / 1024).toFixed(2)}MB`
        );

        setFile(compressedFile);
      } catch (error) {
        console.error("Error compressing image:", error);
        // Fallback to original file if compression fails
        setFile(selectedFile);
      }
    }
  };

  const handleSubmit = async () => {
    if (!form.location_id || form.task_name.length === 0) {
      alert("Please select a purpose, at least one location");
      return;
    }

    // Validate other_purpose if "Others" is selected
    if (form.location_id === "Others" && !form.other_purpose.trim()) {
      alert("Please specify the purpose when selecting 'Others'.");
      return;
    }

    const formData = new FormData();
    formData.append("location_id", form.location_id); // send purpose id
    formData.append("task_name", form.task_name.join(",")); // convert to comma-separated string
    formData.append("createTaskDate", form.createTaskDate);
    formData.append("endTaskDate", form.endTaskDate);
    formData.append("start_time", form.start_time);
    formData.append("end_time", form.end_time);
    formData.append("date", form.date);
    formData.append("time", form.time);
    formData.append("status", "pending");
    formData.append("photo", file);

    // Only include other_purpose in API call if "Others" is selected
    if (form.location_id === "Others") {
      formData.append("other_purpose", form.other_purpose);
    }

    await dispatch(createTaskSubmit(formData));

    // ✅ Refetch tasks from parent
    if (typeof onTaskAdded === "function") {
      onTaskAdded();
    }

    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
      {/* Modal container with proper height constraints */}
      <div className="bg-gradient-to-br from-teal-400 to-cyan-500 rounded-2xl w-full max-w-md text-white relative max-h-[90vh] flex flex-col">
        {/* Header - fixed at top */}
        <div className="flex justify-between items-center p-6 pb-4 flex-shrink-0">
          <h2 className="text-xl font-bold text-black">Ask For Approval</h2>
          <button
            onClick={onClose}
            className="text-gray-700 hover:text-gray-900 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto px-6 pb-4">
          <div className="space-y-4">
            {/* Purpose Dropdown */}
            <div>
              <label className="text-sm block mb-2">Select visit purpose</label>
              <div className="relative text-black">
                <ClipboardList className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 z-10" />
                <select
                  className="w-full p-3 pl-10 rounded-md appearance-none"
                  value={form.location_id}
                  onChange={handlePurposeChange}
                >
                  <option value="">Select purpose</option>
                  {tasks
                    .filter((task) => task.title.toLowerCase() !== "others")
                    .map((task) => (
                      <option key={task.id} value={task.id}>
                        {task.title}
                      </option>
                    ))}
                  <option value="Others">Others</option>
                </select>
              </div>
            </div>

            {/* Conditional input field for "Others" */}
            {showOtherInput && (
              <div>
                <label className="text-sm block mb-2">Specify other purpose</label>
                <input
                  type="text"
                  name="other_purpose"
                  value={form.other_purpose}
                  onChange={handleChange}
                  placeholder="Enter custom purpose"
                  className="w-full p-3 rounded-md text-black"
                />
              </div>
            )}

            {/* Multi-select Location */}
            <div>
              <label className="text-sm block mb-2">Select locations</label>
              <select
                multiple
                className="w-full p-3 text-black rounded-md min-h-[100px] max-h-[120px]"
                onChange={handleLocationChange}
              >
                {locations.map((loc) => (
                  <option key={loc._id} value={loc._id} className="py-1">
                    {loc.location_name}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-200 mt-1">Hold Ctrl/Cmd to select multiple locations</p>
            </div>

            {/* Date and Time Grid */}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-sm block mb-1">Start Date</label>
                <div className="relative text-black">
                  <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 z-10" size={16} />
                  <input
                    type="date"
                    name="createTaskDate"
                    value={form.createTaskDate}
                    onChange={handleChange}
                    className="w-full pl-10 p-2 rounded-md text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="text-sm block mb-1">Start Time</label>
                <div className="relative text-black">
                  <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 z-10" size={16} />
                  <input
                    type="time"
                    name="start_time"
                    value={form.start_time}
                    onChange={handleChange}
                    className="w-full pl-10 p-2 rounded-md text-sm"
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-sm block mb-1">End Date</label>
                <div className="relative text-black">
                  <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 z-10" size={16} />
                  <input
                    type="date"
                    name="endTaskDate"
                    value={form.endTaskDate}
                    onChange={handleChange}
                    className="w-full pl-10 p-2 rounded-md text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="text-sm block mb-1">End Time</label>
                <div className="relative text-black">
                  <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 z-10" size={16} />
                  <input
                    type="time"
                    name="end_time"
                    value={form.end_time}
                    onChange={handleChange}
                    className="w-full pl-10 p-2 rounded-md text-sm"
                  />
                </div>
              </div>
            </div>

            {/* File Upload with Compression */}
            <div>
              <label className="text-sm block mb-2">Upload Photo (Optional)</label>
              <input
                type="file"
                id="uploadPhoto"
                onChange={handleFileChange}
                accept=".jpg,.jpeg,.png,.pdf"
                capture="environment"
                className="hidden"
              />
              <label htmlFor="uploadPhoto">
                <div className="w-full flex items-center justify-center gap-2 py-3 bg-white text-sky-600 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                  <UploadCloud size={18} /> Upload Photo
                </div>
              </label>
              {file && (
                <div className="mt-2 p-2 bg-green-100 rounded-md">
                  <p className="text-sm text-green-800 font-medium">Selected: {file.name}</p>
                  <p className="text-xs text-green-600">
                    Size: {(file.size / 1024 / 1024).toFixed(2)}MB
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer - fixed at bottom */}
        <div className="p-6 pt-4 flex-shrink-0">
          <button
            className="w-full bg-yellow-400 text-black font-semibold py-3 rounded-md hover:bg-yellow-500 transition-colors"
            onClick={handleSubmit}
          >
            Send for Approval
          </button>
        </div>
      </div>
    </div>
  );
};

export default TaskApprovalModalPage;